import { type AdminCustomerCart, type Scan, sealedLineItem } from "@hobbyshopify/backend-client";
import { cardTitle, formatCents } from "./card-display";

type CartLine = AdminCustomerCart["items"][number];

/**
 * The customer's open cart's line for a scan: by the line's scan id, else by
 * card. A line from before offers carried a scan is sent with the offer id in
 * `scanId`, so that one is matched by card too.
 */
export function cartLineForScan(
  carts: readonly AdminCustomerCart[],
  scan: Pick<Scan, "id" | "cardId" | "customerId">,
): { cart: AdminCustomerCart; item: AdminCustomerCart["items"][number] } | null {
  for (const cart of carts) {
    if (cart.status !== "OPEN" || cart.user.id !== scan.customerId) continue;
    const item = cart.items.find((i) =>
      i.scanId && i.scanId !== i.offerId
        ? i.scanId === scan.id
        : scan.cardId !== null && i.offer.cardId === scan.cardId,
    );
    if (item) return { cart, item };
  }
  return null;
}

/**
 * A confirmed scan whose quote still stands can be sold; one past its deadline, sold or never quoted cannot, and a
 * scan no card was recognised in never.
 */
export function canAddToCart(scan: Pick<Scan, "review" | "quoteState" | "recognitionOutcome">): boolean {
  return scan.recognitionOutcome !== "no_match" && scan.review === "CONFIRMED" && scan.quoteState === "active";
}

/** What a customer's scan is quoted at, as iOS shows it: the card's price, else the estimate. */
export function quotedCents(scan: Pick<Scan, "card" | "estimatedPriceCents">): number | null {
  return scan.card?.currentPriceCents ?? scan.estimatedPriceCents;
}

/** "Quoted to Sam at $42.00 · in their cart", over the card a customer's scan names. */
export function quoteNote(
  label: string,
  scan: Pick<Scan, "card" | "estimatedPriceCents" | "quoteState">,
  inCart: boolean,
): string {
  const cents = quotedCents(scan);
  const at = cents == null ? "" : ` at ${formatCents(cents)}`;
  const where = inCart ? " · in their cart" : scan.quoteState === "expired" ? " · quote expired" : "";
  return `Quoted to ${label}${at}${where}`;
}

/** The cards the Checkout bar counts: the included lines' copies. */
export function checkoutCardCount(cart: AdminCustomerCart): number {
  return cart.items.filter((i) => i.includedInPurchaseOrder).reduce((n, i) => n + i.quantity, 0);
}

/** What the Checkout bar charges: Stripe's taxed total once a checkout was priced with tax, else the included lines. */
export function checkoutBarCents(cart: AdminCustomerCart): number {
  const priced = cart.checkout;
  return priced?.amountTotal != null && (priced.taxAmount ?? 0) > 0 ? priced.amountTotal : cartLinesTotalCents(cart);
}

/** What the open cart charges for its included lines, before tax and fees. */
export function cartLinesTotalCents(cart: AdminCustomerCart): number {
  return cart.items
    .filter((i) => i.includedInPurchaseOrder)
    .reduce((sum, i) => sum + i.offer.priceCents * i.quantity, 0);
}

/** The customer list's "Cart $X.XX": each customer's open carts, summed, as iOS's row. Customers with none are left out. */
export function openCartTotals(carts: readonly AdminCustomerCart[]): ReadonlyMap<string, number> {
  const totals = new Map<string, number>();
  for (const cart of carts) {
    if (cart.status !== "OPEN") continue;
    totals.set(cart.user.id, (totals.get(cart.user.id) ?? 0) + cartLinesTotalCents(cart));
  }
  return totals;
}

/** A line's name: a sealed product by its own title, a card by its card details. */
export function cartLineTitle(item: CartLine): string {
  return sealedLineItem(item)?.title ?? cardTitle(item.offer.card);
}

/** A line's second row: year and set for a card, the product's own subtitle and year for a box. */
export function cartLineSubtitle(item: CartLine): string {
  const sealed = sealedLineItem(item);
  if (sealed) return [sealed.subtitle, sealed.year].filter(Boolean).join(" · ");
  const card = item.offer.card;
  return [card.year || null, card.setName, card.parallelType].filter(Boolean).join(" · ");
}

export function cartLineImage(item: CartLine): string | null {
  const sealed = sealedLineItem(item);
  return sealed ? sealed.imageUrl : item.offer.card.imageUrl;
}

/**
 * What the shop has to know about a line's stock before handing it over: units
 * a payment found missing, or, on an open cart, counted units nothing is held for.
 */
export function lineStockNote(item: CartLine, cartStatus: AdminCustomerCart["status"]): string | null {
  if (item.shortQuantity > 0) return `Short ${item.shortQuantity}`;
  if (cartStatus !== "OPEN") return null;
  const counted = sealedLineItem(item)?.trackStock ?? item.offer.card.trackStock;
  const unheld = item.quantity - item.heldQuantity;
  if (!counted || unheld <= 0) return null;
  return item.quantity === 1 ? "No copy held" : `${unheld} not held`;
}

/** A change to a cart failed on its way: it may have gone through, so the cart was read again. */
export const CART_CHANGE_FAILED_ON_THE_WAY =
  "Something went wrong while changing the cart. It shows what went through: check it before trying again.";
export const CART_CHANGE_FAILED_UNREAD =
  "Something went wrong while changing the cart, and it could not be read again. Read it again before changing it.";
export const CART_DELETE_FAILED_ON_THE_WAY =
  "Something went wrong while deleting the cart. Try again if it is still here.";
export const CART_DELETE_FAILED_UNREAD =
  "Something went wrong while deleting the cart, and it could not be read again. Read it again to see if it is gone.";

/** A Cancel the card reader did not take: the collection goes on, and what it reads still counts. */
export const READER_DID_NOT_STOP = "The reader did not stop, so it is still waiting for the card. Press Cancel again.";

/** The sentences that say the cart could not be read: a read that answers makes them untrue. */
export const CART_UNREAD: readonly string[] = [CART_CHANGE_FAILED_UNREAD, CART_DELETE_FAILED_UNREAD];
